import React from 'react';

import { localStorageUtils } from '../../utils/local-storage-utils';
import { userStateAdapter } from '../state/user-state/user-state-adapter';

type BooleanSettingKey =
  | 'designToolSnappingDialog'
  | 'snappingDialogTooltip'
  | 'designToolAllHeights'
  | 'designToolTooltipSnapSettings'
  | 'snappingDialogAllHeights';

/**
 * A setting that is on or off, remembered for the user in the browser. Off until it is set, unless
 * on by default.
 */
export const useStoredSetting = (key: BooleanSettingKey, defaultValue = false) => {
  const read = () => localStorageUtils.getUserItemOrDefault(key, String(defaultValue)) === 'true';
  const [value, setValue] = React.useState(read);

  // Read again once the user has loaded, or another one is active: whose setting it is
  const userId = React.useSyncExternalStore(
    (listener) => userStateAdapter.subscribe(listener),
    () => {
      const snapshot = userStateAdapter.getSnapshot();
      return snapshot.isLoaded ? snapshot.getActiveUser()?.id : undefined;
    },
  );
  React.useEffect(() => {
    if (userId) {
      setValue(read());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, key]);

  const setStoredValue = (newValue: boolean) => {
    setValue(newValue);
    localStorageUtils.setUserItem(key, newValue.toString());
  };

  return [value, setStoredValue] as const;
};
